import React from "react";
import { useParams } from "react-router-dom";
import { Grid, Typography, Select, CircularProgress, Box, Stack, TextField, Button } from "@mui/material";

import { UserType, FeatureId, LLMRetryAttempts, LLMRetryIntervals, LLMAPIProvider, isValidJSON } from "../../../utils/constants";

import SelectLLMProvider from "../../../components/pages/llm/guard/L_SelectProvider";

import { Root, MenuItem, IOSSwitch, SnackbarAlert } from "../../../components/pages/common/styled";

import useZone from "../../../hooks/user/useZone";
import useAuth from "../../../hooks/useAuth";
import useLLMConfig from "../../../hooks/user/useLLMConfig";
import LLMConfigHeader from "./llmHeader";

function LLMConfig() {
  const { configZone } = useParams();
  const zoneUid = configZone;
  const {
    isAuthenticated,
    homeController,
    wafdashController,
    webzoneController,
    wafeventController,
    planController,
    userType,
    isFeatureEnabled,
  } = useAuth();
  const { getZonesForItems } = useZone();
  const { llmConfig, getLLMConfig, configLLMSetting, errMsg, setErr } = useLLMConfig();

  const [active, set_active] = React.useState(false);
  const [request_payload, set_request_payload] = React.useState(false);
  const [use_cache, set_use_cache] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [prompt_scan_active, set_prompt_scan_active] = React.useState(false);
  const [output_scan_active, set_output_scan_active] = React.useState(false);
  const [fallback_enable, set_fallback_enable] = React.useState(false);
  const [fallback_retry_attempts, set_fallback_retry_attempts] = React.useState(0);
  const [fallback_retry_interval, set_fallback_retry_interval] = React.useState(0);
  const [fallback1, set_fallback1] = React.useState("");
  const [fallback2, set_fallback2] = React.useState("");

  const [provider_type, set_provider_type] = React.useState(LLMAPIProvider.OPEN_AI);
  const [prompt_filter, set_prompt_filter] = React.useState("");
  const [output_filter, set_output_filter] = React.useState("");

  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      getZonesForItems();
    }
    return () => setErr(null);
  }, [isAuthenticated, getZonesForItems, setErr]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      if (homeController) homeController.abort();
      if (wafdashController) wafdashController.abort();
      if (webzoneController) webzoneController.abort();
      if (wafeventController) wafeventController.abort();
      if (planController) planController.abort();
      if (zoneUid) {
        getLLMConfig(zoneUid, true);
      }
    }
  }, [isAuthenticated, zoneUid]); // eslint-disable-line react-hooks/exhaustive-deps

  const [snackOpen, setSnackOpen] = React.useState(false);
  const [message, setMessage] = React.useState();
  const [success, setSuccess] = React.useState();
  const handleSnackClose = () => {
    setSnackOpen(false);
    setErr(null);
  };

  React.useEffect(() => {
    if (errMsg) {
      setSnackOpen(true);
      setMessage(errMsg);
      setSuccess("error");
    } else {
      setSnackOpen(false);
      setMessage(null);
    }
  }, [errMsg]);

  React.useEffect(() => {
    if (llmConfig) {
      set_active(llmConfig?.active);
      set_request_payload(llmConfig?.audit_req_body);
      set_use_cache(llmConfig?.use_cache);
      set_prompt_scan_active(llmConfig?.prompt_scan_active);
      set_output_scan_active(llmConfig?.output_scan_active);
      set_fallback_enable(llmConfig?.fallback?.enabled);
      set_fallback_retry_attempts(llmConfig?.fallback?.retry_attempts);
      set_fallback_retry_interval(llmConfig?.fallback?.retry_interval);
      set_fallback1(llmConfig?.fallback?.url1);
      set_fallback2(llmConfig?.fallback?.url2);
      set_prompt_filter(llmConfig?.llm_provider?.prompt_filter);
      set_output_filter(llmConfig?.llm_provider?.output_filter);
      set_provider_type(llmConfig?.llm_provider?.type);
    }
  }, [llmConfig]);

  const change_active = async () => {
    if (userType === UserType.ORG_USER) return;
    const _active = !active;
    set_active(_active);
    setSaving(true);
    await configLLMSetting(zoneUid, "change_active", { enable: _active });
    setSaving(false);
  };
  const requestPayloadChange = async () => {
    if (userType === UserType.ORG_USER) return;
    const _request_payload = !request_payload;
    set_request_payload(_request_payload);
    setSaving(true);
    await configLLMSetting(zoneUid, "requestPayloadChange", { req_body_enabled: _request_payload });
    setSaving(false);
  };
  const useCacheChange = async () => {
    if (userType === UserType.ORG_USER) return;
    const _use_cache = !use_cache;
    set_use_cache(_use_cache);
    setSaving(true);
    await configLLMSetting(zoneUid, "useCacheChange", { use_cache: _use_cache });
    setSaving(false);
  };
  const requestScanActiveChange = async () => {
    if (userType === UserType.ORG_USER) return;
    const _prompt_scan_active = !prompt_scan_active;
    set_prompt_scan_active(_prompt_scan_active);
    setSaving(true);
    await configLLMSetting(zoneUid, "requestScanActiveChange", { prompt_scan_active: _prompt_scan_active });
    setSaving(false);
  };
  const responseScanActiveChange = async () => {
    if (userType === UserType.ORG_USER) return;
    const _output_scan_active = !output_scan_active;
    set_output_scan_active(_output_scan_active);
    setSaving(true);
    await configLLMSetting(zoneUid, "responseScanActiveChange", { output_scan_active: _output_scan_active });
    setSaving(false);
  };
  const fallbackEnableChange = async () => {
    if (userType === UserType.ORG_USER) return;
    const _fallback_enable = !fallback_enable;
    set_fallback_enable(_fallback_enable);
    setSaving(true);
    await configLLMSetting(zoneUid, "fallbackEnableChange", { fallback_enable: _fallback_enable });
    setSaving(false);
  };
  const fallbackRetryAttemptsChange = async (event) => {
    if (userType === UserType.ORG_USER) return;
    const _fallback_retry_attempts = event.target.value;
    set_fallback_retry_attempts(_fallback_retry_attempts);
    setSaving(true);
    await configLLMSetting(zoneUid, "fallbackRetryAttemptsChange", { fallback_retry_attempts: _fallback_retry_attempts });
    setSaving(false);
  };
  const fallbackRetryIntervalChange = async (event) => {
    if (userType === UserType.ORG_USER) return;
    const _fallback_retry_interval = event.target.value;
    set_fallback_retry_interval(_fallback_retry_interval);
    setSaving(true);
    await configLLMSetting(zoneUid, "fallbackRetryIntervalChange", { fallback_retry_interval: _fallback_retry_interval });
    setSaving(false);
  };
  const updateFallbackUrls = async () => {
    if (userType === UserType.ORG_USER) return;
    setSaving(true);
    await configLLMSetting(zoneUid, "fallbackUrlsChange", { fallback1, fallback2 });
    setSaving(false);
  };

  const updateLLMProvider = async (type, prompt_filter_value, output_filter_value) => {
    if (userType === UserType.ORG_USER) return;
    if (type === LLMAPIProvider.CUSTOM) {
      if (prompt_filter_value === "") {
        setMessage("The request format is empty.");
        setSuccess("error");
        setSnackOpen(true);
        return;
      }
      if (!isValidJSON(prompt_filter_value)) {
        setMessage("The request format is invalid.");
        setSuccess("error");
        setSnackOpen(true);
        return;
      }
      if (output_filter_value === "") {
        setMessage("The response format is empty.");
        setSuccess("error");
        setSnackOpen(true);
        return;
      }
      if (!isValidJSON(output_filter_value)) {
        setMessage("The response format is invalid.");
        setSuccess("error");
        setSnackOpen(true);
        return;
      }
    }
    setSaving(true);
    await configLLMSetting(zoneUid, "promptOutputFilters", {
      type,
      prompt_filter: prompt_filter_value,
      output_filter: output_filter_value,
    });
    setSaving(false);
  };

  return (
    <React.Fragment>
      <LLMConfigHeader title={"LLM Configuration"} url={"config"} />
      <Box sx={{ background: "white", borderRadius: "0px 28px 28px 28px", padding: "36px 16px" }}>
        {llmConfig === null ? (
          <Root>
            <CircularProgress color="primary" />
          </Root>
        ) : (
          <Grid container pt={1}>
            <Grid item xs={12} md={4} sx={{ borderRight: "solid 1px #E9E9E9" }}>
              <Grid container spacing={{ xs: 3, lg: 6 }} paddingRight={12}>
                <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h2">LLM Guardrail</Typography>
                  <IOSSwitch disabled={saving} checked={active} onChange={change_active} />
                </Grid>
                <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between">
                  <Typography>Payload Logging</Typography>
                  <IOSSwitch
                    checked={request_payload}
                    disabled={saving || !active || !isFeatureEnabled(FeatureId.LOG_REQUEST_PAYLOAD)}
                    onChange={requestPayloadChange}
                  />
                </Grid>
                <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between">
                  <Typography>Use Cache</Typography>
                  <IOSSwitch
                    checked={use_cache}
                    disabled={saving || !active || !isFeatureEnabled(FeatureId.USE_CACHE)}
                    onChange={useCacheChange}
                  />
                </Grid>
                <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between">
                  <Typography>Request Scan</Typography>
                  <IOSSwitch checked={prompt_scan_active} disabled={saving || !active} onChange={requestScanActiveChange} />
                </Grid>
                <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between">
                  <Typography>Response Scan</Typography>
                  <IOSSwitch checked={output_scan_active} disabled={saving || !active} onChange={responseScanActiveChange} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={8} sx={{ pl: 6 }}>
              <Grid container spacing={{ xs: 3, lg: 6 }}>
                <Grid item xs={12} md={4} pr={12}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" pb={14}>
                    <Typography variant="h2">Use Fall back</Typography>
                    <IOSSwitch checked={fallback_enable} disabled={saving || !active} onChange={fallbackEnableChange} />
                  </Stack>

                  <Stack direction="row" alignItems="center" justifyContent="space-between" pb={8}>
                    <Typography variant="h2">Retry Attempts</Typography>
                    <Select
                      value={fallback_retry_attempts}
                      disabled={saving || !active || !fallback_enable}
                      onChange={fallbackRetryAttemptsChange}
                      sx={{ width: "90px", border: "1px solid #C1C1C1" }}
                    >
                      {LLMRetryAttempts.map((value) => (
                        <MenuItem value={value} key={`fallback_retry_attempts_${value}`}>
                          {value}
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h2">Retry Interval</Typography>
                    <Select
                      value={fallback_retry_interval}
                      disabled={saving || !active || !fallback_enable}
                      onChange={fallbackRetryIntervalChange}
                      sx={{ width: "90px", border: "1px solid #C1C1C1" }}
                    >
                      {Object.keys(LLMRetryIntervals).map((key) => (
                        <MenuItem value={LLMRetryIntervals[key]} key={`fallback_retry_attempts_${key}`}>
                          {key}
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography pb={3}>
                      <b>Fall back URLs</b>
                    </Typography>
                    <Button
                      variant="contained"
                      color="success"
                      size="ui"
                      loadingPosition="start"
                      loading={saving}
                      disabled={saving || !active || !fallback_enable}
                      onClick={updateFallbackUrls}
                      sx={{ marginBottom: "16px" }}
                    >
                      Update
                    </Button>
                  </Stack>
                  <Stack spacing={2}>
                    <Typography>Fall back 1</Typography>
                    <TextField
                      fullWidth
                      value={fallback1}
                      placeholder={saving || !active || !fallback_enable ? "" : "https://fallback1.com/api/chat"}
                      disabled={saving || !active || !fallback_enable}
                      onChange={(e) => {
                        set_fallback1(e.target.value);
                      }}
                    />
                    <Typography>Fall back 2</Typography>
                    <TextField
                      fullWidth
                      value={fallback2}
                      placeholder={saving || !active || !fallback_enable ? "" : "https://fallback2.com/api/chat"}
                      disabled={saving || !active || !fallback_enable}
                      onChange={(e) => {
                        set_fallback2(e.target.value);
                      }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sx={{ borderTop: "solid 1px #E9E9E9", mt: 3, pt: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h2">Select Provider</Typography>
                </Grid>
                <Grid item xs={12}>
                  <SelectLLMProvider
                    provider_type={provider_type}
                    set_provider_type={set_provider_type}
                    prompt_filter={prompt_filter}
                    set_prompt_filter={set_prompt_filter}
                    output_filter={output_filter}
                    set_output_filter={set_output_filter}
                    updateLLMProvider={updateLLMProvider}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Box>
      <SnackbarAlert open={snackOpen} onClose={handleSnackClose} severity={success} message={message} />
    </React.Fragment>
  );
}
export default LLMConfig;
